/**
 * Appointment Data Service
 * Manages consistent appointment generation across Dashboard and Calendar
 */

class AppointmentDataService {
  constructor() {
    this.procedures = [
      "Consultation and evaluation",
      "Follow-up examination", 
      "Diagnostic procedure",
      "Surgical consultation",
      "Treatment planning",
      "Post-operative check-up",
      "Routine check-up",
      "Preventive screening",
      "Diagnostic imaging review",
      "Laboratory results review"
    ];
  }

  /**
   * Generate a deterministic seed from physician data
   */
  generateSeed(physician, index = 0) {
    const id = physician.id || physician.npi || '';
    const name = physician.full_name || physician.name || '';
    const seedString = `${id}${name}${index}`;
    
    return seedString.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  }

  /**
   * Generate deterministic random number between min and max
   */
  seededRandom(seed, min = 0, max = 1) {
    const x = Math.sin(seed) * 10000;
    const random = x - Math.floor(x);
    return Math.floor(random * (max - min + 1)) + min;
  }

  /**
   * Generate consistent number of appointments for a physician
   */
  getAppointmentCount(physician, index = 0) {
    const seed = this.generateSeed(physician, index);
    return this.seededRandom(seed, 1, 3); // 1-3 appointments per physician
  }

  /**
   * Generate consistent appointments for a physician
   */
  generateAppointmentsForPhysician(physician, index = 0) {
    const appointments = [];
    const numAppointments = this.getAppointmentCount(physician, index);
    
    for (let i = 0; i < numAppointments; i++) {
      const appointmentSeed = this.generateSeed(physician, index * 100 + i);
      
      // Generate consistent appointment date (always in the future, next 2 weeks)
      const baseDaysFromNow = (index * 2) + (i * 3) + 1; // Start from tomorrow
      const additionalDays = this.seededRandom(appointmentSeed, 0, 7); // Add 0-7 more days
      const daysFromNow = baseDaysFromNow + additionalDays;
      const appointmentDate = new Date(Date.now() + (daysFromNow * 86400000));

      
      // Generate consistent times
      const startHour = this.seededRandom(appointmentSeed + 1, 8, 16); // 8 AM to 4 PM
      const startMinute = this.seededRandom(appointmentSeed + 2, 0, 1) * 30; // 0 or 30 minutes
      const duration = [30, 60, 90][this.seededRandom(appointmentSeed + 3, 0, 2)]; // 30, 60, or 90 minutes
      
      const startTime = `${startHour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}`;
      const endHour = startHour + Math.floor(duration / 60);
      const endMinute = startMinute + (duration % 60);
      const endTime = `${endHour.toString().padStart(2, '0')}:${(endMinute % 60).toString().padStart(2, '0')}`;
      
      // Generate consistent procedure
      const procedureIndex = this.seededRandom(appointmentSeed + 4, 0, this.procedures.length - 1);
      
      const appointment = {
        id: `${physician.id}_${i}`,
        first_name: physician.full_name?.split(' ')[0] || physician.name?.split(' ')[0] || 'Dr.',
        full_name: physician.full_name || physician.name || 'Unknown Physician',
        specialty: physician.specialty || 'General Practice',
        start_date: appointmentDate.toISOString().split('T')[0],
        start_time: startTime,
        end_time: endTime,
        hospital: physician.hospital || physician.organization || 'Unknown',
        procedure: `${physician.specialty || 'General'} ${this.procedures[procedureIndex]}`,
        npi: physician.npi,
        physicianId: physician.id
      };
      
      appointments.push(appointment);
    }
    
    return appointments;
  }

  /**
   * Generate all appointments for a list of physicians
   */
  generateAllAppointments(savedPractitioners) {
    if (!savedPractitioners || !Array.isArray(savedPractitioners)) {
      return [];
    }

    const allAppointments = [];
    
    savedPractitioners.forEach((physician, index) => {
      const physicianAppointments = this.generateAppointmentsForPhysician(physician, index);
      allAppointments.push(...physicianAppointments);
    });
    // Sort by date
    return allAppointments.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
  }

  /**
   * Get all appointments for the next day that has appointments
   */
  getNextDayAppointments(savedPractitioners) {
    if (!savedPractitioners || !Array.isArray(savedPractitioners) || savedPractitioners.length === 0) {
      return [];
    }

    // No longer generate simulated appointments - return empty array
    // Real appointments should come from Firebase or Epic FHIR via centralAppointmentService
    console.log('⚠️ getNextDayAppointments called - this should use centralAppointmentService instead');
    return [];
  }

  /**
   * Generate dashboard data - NO LONGER GENERATES SIMULATED DATA
   * Returns basic practitioner info without fake appointment counts
   */
  generateDashboardData(savedPractitioners) {
    if (!savedPractitioners || !Array.isArray(savedPractitioners)) {
      return [];
    }

    return savedPractitioners.map(practitioner => {
      return {
        id: practitioner.id,
        full_name: practitioner.full_name || practitioner.name || 'Unknown Physician',
        specialty: practitioner.specialty || 'General Practice',
        hospital: practitioner.hospital || practitioner.organization || 'Unknown',
        cases_completed: 0, // No simulated data - real counts should come from centralAppointmentService
        next_appointment: null, // No simulated data - real dates should come from centralAppointmentService
        npi: practitioner.npi,
        originalData: practitioner
      };
    });
  }

  /**
   * Generate monthly chart data for dashboard
   */
  generateMonthlyChartData(appointments) {
    const monthlyData = {};
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Initialize all months with 0
    months.forEach(month => {
      monthlyData[month] = 0;
    });
    
    // Count appointments by month
    appointments.forEach(appointment => {
      try {
        const date = new Date(appointment.start_date || appointment.start);
        const monthIndex = date.getMonth();
        const monthName = months[monthIndex];
        if (monthName) {
          monthlyData[monthName]++;
        }
      } catch (error) {
        console.warn('Error processing appointment date for chart:', error);
      }
    });
    
    // Convert to array format for charts
    return months.map(month => ({
      month,
      appointments: monthlyData[month]
    }));
  }
}

export default AppointmentDataService; 